/**
 * Scroll Animations Module
 * Simple intersection observer for enter animations
 */

interface AnimationConfig {
  rootMargin?: string;
  threshold?: number;
  staggerDelay?: number;
}

/**
 * Manages scroll-triggered animations using IntersectionObserver
 * Supports both single elements and groups with stagger effects
 */
class ScrollAnimations {
  private observer: IntersectionObserver;
  private animated = new WeakSet<Element>();
  private config: Required<AnimationConfig>;

  constructor(config: AnimationConfig = {}) {
    this.config = {
      rootMargin: config.rootMargin || '0px 0px -10% 0px',
      threshold: config.threshold || 0.1,
      staggerDelay: config.staggerDelay || 100
    };

    this.observer = new IntersectionObserver(
      entries => this.handleIntersection(entries),
      {
        rootMargin: this.config.rootMargin,
        threshold: this.config.threshold
      }
    );

    this.init();
  }

  private init(): void {
    // Check for reduced motion preference
    if (this.prefersReducedMotion()) {
      return;
    }

    // Skip animations entirely in editor iframes
    // The CSS ensures elements are visible, so we don't need JS animations
    if (window.frameElement) {
      return;
    }

    // Find and observe all animatable elements
    this.observeElements();
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private observeElements(): void {
    // Single elements
    const singles = document.querySelectorAll('[data-animate]');
    singles.forEach(el => {
      this.observer.observe(el);
    });

    // Groups
    const groups = document.querySelectorAll('[data-animate-group]');
    groups.forEach(group => {
      const children = group.children;
      Array.from(children).forEach(child => {
        this.observer.observe(child);
      });
    });
  }

  /**
   * Public API: Observe new elements for animations
   * Used when content is dynamically added (e.g., infinite scroll)
   * @param container Optional container element to search within
   */
  public observeNewElements(container?: Element): void {
    // Check for reduced motion preference
    if (this.prefersReducedMotion()) {
      return;
    }

    // Skip animations entirely in editor iframes
    if (window.frameElement) {
      return;
    }

    const root = container || document.body;
    
    // If container itself is an animate-group
    if (container?.hasAttribute('data-animate-group')) {
      Array.from(container.children).forEach(child => {
        if (!this.animated.has(child)) {
          this.observer.observe(child);
        }
      });
    } else {
      // Look for animate-group containers within root
      const groups = root.querySelectorAll('[data-animate-group]');
      groups.forEach(group => {
        Array.from(group.children).forEach(child => {
          if (!this.animated.has(child)) {
            this.observer.observe(child);
          }
        });
      });
      
      // Also look for single animated elements
      const singles = root.querySelectorAll('[data-animate]');
      singles.forEach(el => {
        if (!this.animated.has(el)) {
          this.observer.observe(el);
        }
      });
    }
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.animated.has(entry.target)) {
        this.animateElement(entry.target);
      }
    });
  }

  private animateElement(element: Element): void {
    // Mark as animated
    this.animated.add(element);
    
    // Get animation type
    const parent = element.parentElement;
    const animationType = element.getAttribute('data-animate') || 
                         parent?.getAttribute('data-animate-group');
    
    if (!animationType) return;

    // Check if part of a group
    if (parent?.hasAttribute('data-animate-group')) {
      this.animateGroupChild(element, animationType);
    } else {
      // Animate single element immediately
      element.classList.add('animate-active');
    }

    // Unobserve for performance
    this.observer.unobserve(element);
  }

  private animateGroupChild(element: Element, animationType: string): void {
    const parent = element.parentElement!;
    
    // Skip stagger on mobile
    if (window.innerWidth < 768) {
      element.classList.add('animate-active');
      return;
    }
    
    // Get all children in the viewport
    const rect = element.getBoundingClientRect();
    const elementTop = rect.top;
    
    // Find siblings in the same visual row (within 10px tolerance)
    const rowSiblings = Array.from(parent.children).filter(child => {
      const childRect = child.getBoundingClientRect();
      return Math.abs(childRect.top - elementTop) < 10 && 
             childRect.top < window.innerHeight;
    });
    
    // Only stagger within the same row
    const indexInRow = rowSiblings.indexOf(element);
    const delay = indexInRow >= 0 ? indexInRow * this.config.staggerDelay : 0;

    setTimeout(() => {
      element.classList.add('animate-active');
    }, delay);
  }
}

// Store instance to prevent multiple initializations
let instance: ScrollAnimations | null = null;

/**
 * Initialize scroll animations for the page
 * @param config Optional configuration overrides
 */
export function initScrollAnimations(config?: AnimationConfig): void {
  // Prevent multiple initializations
  if (instance) {
    return;
  }
  
  instance = new ScrollAnimations(config);
}

/**
 * Observe new elements for animations (e.g., after AJAX load)
 * @param container Optional container element to search within
 */
export function observeNewAnimations(container?: Element): void {
  if (instance) {
    instance.observeNewElements(container);
  }
}